import React, {
  useState, useEffect, useRef,
} from 'react';
import { getEnv, navigate } from '@/common/agents';
import { message } from '$/components/Message';
import { useOfficialStore } from '$/store/useOfficialStore';
import { type OfficialMenuGroup } from '@/apiSdk/api.type';
import { useCommonStore } from '@/store/useCommonStore';
import preQrCode from '@/assets/pre-qrcode.png';
import betaQrCode from '@/assets/beta-qrcode.png';
import relaseQrCode from '@/assets/release-qrcode.png';
import ServerHeader, { type OverseaOfficialHeaderAppearance } from './ServerHeader';
import styles from './index.module.scss';

/**
 * 官网导航的状态包装层，参与 SSR，并在挂载后更新登录态和滚动遮罩。
 * 接口分组原样传给 ServerHeader；此处补充登录回调和下载弹窗内容，不维护分组展开状态。
 */
export default function ClientHeader({
  showAlert,
  menus,
  appearance,
}: {
  showAlert?: boolean;
  menus: OfficialMenuGroup[];
  appearance?: OverseaOfficialHeaderAppearance;
}) {
  const [scrolled, setScrolled] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [mounted, setMounted] = useState(false);
  const toLogin = useCommonStore((state) => state.toLogin);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());

  // 确保首次渲染与服务端一致，mount 后再使用真实登录态
  useEffect(() => {
    window.scrollTo(0, 0);
    setMounted(true);
    console.log('[菜单数据]', menus);
  }, []);

  // 登录成功后直接进入工作台，避免刷新官网时因服务端读不到客户端账号态而仍停留在当前页。
  const handleLogin = async () => {
    window.mtstat?.track('roboneo_official_page_icon_click', {
      icon_id: 'login',
    });
    try {
      await toLogin();
      navigate({
        mode: 'url',
        url: getEnv().ENV === 'development' ? '/--roboneo/home' : '/home',
        replace: true,
      });
    } catch (error) {
      console.error('登录失败:', error);
      message.error('RoboNeo_Web_network_fail_retry');
    }
  };

  //   22 是alert高度的一半。看最终视觉效果，可以微调，无逻辑影响
  const SCROLL_ALL_DISTANCE = showAlert ? 22 : 100;

  useEffect(() => {
    // 在页面顶部100px处创建一个占位元素，当它离开视口时表示已滚动超过100px
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        console.log('entries', entries[0].isIntersecting, window.scrollY || window.pageYOffset);
        // 当占位元素不可见时，说明已滚动超过100px
        setScrolled(!entries[0].isIntersecting);
      },
      {
        // rootMargin: '-100px 0px' 表示在视口顶部100px处设置一个观察点
        rootMargin: `${SCROLL_ALL_DISTANCE - 1}px 0px 0px 0px`,
        threshold: 0,
      },
    );

    observer.observe(sentinel);

    // 初始检查一次，处理页面加载时已经滚动的情况
    const scrollY = window.scrollY || window.pageYOffset;
    setScrolled(scrollY >= 100);

    const cleanup = () => {
      observer.disconnect();
    };
    return cleanup;
  }, [SCROLL_ALL_DISTANCE]);

  const downloadContent = () => {
    const QR_CODE = {
      development: preQrCode,
      dev: preQrCode,
      pre: preQrCode,
      beta: betaQrCode,
      release: relaseQrCode,
    }[getEnv().ENV];

    return (
      <div className={styles.downloadPopverContent}>
        <div className={styles.qrcodeBox}>
          <img className={styles.qrcode} src={QR_CODE.src} alt="qrcode" />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* 占位元素，用于 IntersectionObserver 监听 */}
      <div
        ref={sentinelRef}
        className={styles.sentinel}
      />
      <ServerHeader
        showAlert={showAlert}
        scrolled={scrolled}
        appearance={appearance}
        downloadContent={downloadContent()}
        menus={menus}
        onLoginClick={handleLogin}
        isLoggedIn={mounted && isUserLoggedIn}
      />
    </>
  );
}
